import * as React from 'react';

import { Button } from '../../primitives/Button/Button';
import type { ButtonVariant } from '../../primitives/Button/buttonTypes';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { confirm } from '../../integrations/confirm';
import { useModalContext } from './Modal';
import { resolveIntent } from './modalActionPresets';
import type { ModalActionIntent } from './modalActionPresets';

/* ─────────────────────────── Modal.Cancel ─────────────────────────── */

const DEFAULT_CONFIRM_MESSAGE = 'Your changes will not be saved. Are you sure you want to close?';

export interface ModalCancelProps {
  /** When true, shows a confirm dialog before closing. */
  dirty?: boolean;
  /** Confirm dialog message. */
  confirmMessage?: string;
  /** Additional handler (runs before close logic). */
  onClick?: () => void;
  disabled?: boolean;
  /** Button text. Default: "Cancel" */
  label?: string;
  semanticId?: string;
  /** Override entire button rendering. */
  children?: React.ReactNode;
}

export const ModalCancel = React.forwardRef<HTMLButtonElement, ModalCancelProps>(
  function ModalCancel(
    { dirty, confirmMessage, onClick, disabled, label, semanticId, children },
    ref,
  ) {
    const { onOpenChange } = useModalContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = React.useCallback(async () => {
      onClick?.();

      if (dirty) {
        const { confirmed } = await confirm({
          title: confirmMessage ?? DEFAULT_CONFIRM_MESSAGE,
        });
        if (!confirmed) return;
      }

      onOpenChange(false);
    }, [dirty, confirmMessage, onClick, onOpenChange]);

    if (children != null) {
      return (
        <span role="none" onClick={handleClick} {...semanticProps}>
          {children}
        </span>
      );
    }

    return (
      <Button
        ref={ref}
        type="button"
        variant="primary-dark"
        label={label ?? 'Cancel'}
        onClick={handleClick}
        disabled={disabled}
        {...semanticProps}
      />
    );
  },
);

ModalCancel.displayName = 'Modal.Cancel';

/* ─────────────────────────── Modal.Action ─────────────────────────── */

export interface ModalActionProps {
  /** Semantic intent — auto-resolves label, icon, and color. Default: "save" */
  intent?: ModalActionIntent;
  /** Override the auto-resolved label. */
  label?: string;
  /** Override the auto-resolved icon. */
  icon?: string;
  /** Override the auto-resolved ButtonVariant. */
  variant?: ButtonVariant;
  /** HTML button type. Default derived from intent. */
  type?: 'button' | 'submit';
  /**
   * Associates the button with a `<form>` by its `id`, allowing submission
   * from outside the form DOM tree. Maps to the native `form` attribute.
   */
  form?: string;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  semanticId?: string;
  /** Override entire button rendering. */
  children?: React.ReactNode;
}

export const ModalAction = React.forwardRef<HTMLButtonElement, ModalActionProps>(
  function ModalAction(
    { intent = 'save', label, icon, variant, type, form, onClick, loading, disabled, semanticId, children },
    ref,
  ) {
    const resolved = resolveIntent(intent, { label, icon, variant, type });
    const semanticProps = getSemanticAttributes(semanticId);

    if (children != null) {
      return (
        <span role="none" {...semanticProps}>
          {children}
        </span>
      );
    }

    return (
      <Button
        ref={ref}
        type={resolved.type}
        variant={resolved.variant}
        label={resolved.label}
        icon={resolved.icon}
        onClick={onClick}
        loading={loading}
        disabled={disabled}
        form={form}
        {...semanticProps}
      />
    );
  },
);

ModalAction.displayName = 'Modal.Action';
